import React, { useState } from "react";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import {
  AutocompleteInput,
  FileInput,
  required,
  TextInput,
  useNotify,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { FileField } from "../../../components/common/FileField";
import { AttachFileIcon } from "../../../components/common/iconography";
import SwitchOnOff from "../../../components/common/AntSwitch";
import { HelpViewer } from "../../../components/common/HelpViewer/HelpViewer";
import { HelpTopicOptions } from "../../../utils/constants";
import { cleanUrlLink } from "../../../utils/utils";
import { handleFileChange } from "../../../utils/fileUtils";

const validateRequired = [required()];

const EXAMPLE_PLACEHOLDER = `# Title
Lorem *ipsum* dolor sit amet, consectetur **adipiscing** elit.

- Video [video](video/video_id) Example Text.
- Tour [tour](tour/tour_id) Example Text.
- Custom [custom](custom) Example Text.
- Link [google](google.com) Example Text.
`;

const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: theme.spacing(3),
  },
  markdownViewer: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #6e6e6e",
    marginTop: theme.spacing(1),
    minHeight: theme.spacing(45.125),
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    overflowY: "scroll",
  },
  booleanInput: {
    marginTop: theme.spacing(2),
  },
}));

export const HelpPageForm = ({ record = {}, location }) => {
  const [markdown, setMarkdown] = useState(
    record?.content || location?.state?.clone?.content || ""
  );
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const { values } = useFormState({ subscription: { values: true } });

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "isLive",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true }
  );

  const handleDraftLiveChange = (value) => {
    form.change("isLive", value);
  };

  const insertTokenIntoContent = (token) => {
    const formState = form.getState();
    const currentContent = formState?.values?.content || "";
    const bullet = `- ${token}`;
    const needsNewline =
      currentContent.length > 0 && !currentContent.endsWith("\n");
    const updated = `${currentContent}${needsNewline ? "\n" : ""}${bullet}`;
    form.change("content", updated);
    setMarkdown(updated);
  };

  return (
    <Grid className={classes.formContainer}>
      <Typography variant="h6" gutterBottom>
        Edit Help Page
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextInput
            source="name"
            label="Name"
            variant="outlined"
            fullWidth
            validate={validateRequired}
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput
            source="url"
            label="URL"
            variant="outlined"
            fullWidth
            parse={cleanUrlLink}
            disabled={!!record.url}
            validate={validateRequired}
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AutocompleteInput
            source="topic"
            label="Topic"
            variant="outlined"
            choices={HelpTopicOptions}
            fullWidth
            validate={validateRequired}
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="content"
            label="Content"
            variant="outlined"
            multiline
            minRows={18}
            fullWidth
            onChange={(e) => setMarkdown(e.target.value)}
            placeholder={EXAMPLE_PLACEHOLDER}
            validate={validateRequired}
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <div className={classes.markdownViewer}>
            <HelpViewer
              helpPage={{
                content: markdown,
                kbFiles: record?.kbFiles,
                kbMedia: record?.kbMedia,
              }}
            />
          </div>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="overline"
            color="textSecondary"
            style={{ display: "block" }}
          >
            Release status
          </Typography>
          <SwitchOnOff
            leftLabel="dev draft"
            rightLabel="live in prod"
            checked={Boolean(values?.isLive)}
            setChecked={handleDraftLiveChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="overline" color="textSecondary" gutterBottom>
            Knowledge Base Attachments
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Upload documents and media, then link to them from the Content field
            using markdown tokens like <code>[My PDF](kbfile/MyDoc.pdf)</code>{" "}
            or <code>[Intro Video](kbmedia/intro.mp4)</code>. The filename must
            match the uploaded file&apos;s name.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FileInput
            source="kbFiles"
            label="KB Documents"
            variant="outlined"
            fullWidth
            multiple
            placeholder={
              <p>
                <AttachFileIcon fontSize="large" /> <br /> Drop file(s) to
                upload, or click to select.
              </p>
            }
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={(value) =>
              handleFileChange("kbFiles", value, form, notify)
            }
            helperText={false}
          >
            <FileField
              source="src"
              title="title"
              target="_blank"
              tokenPrefix="kbfile"
              onAddToken={insertTokenIntoContent}
            />
          </FileInput>
        </Grid>
        <Grid item xs={12} md={6}>
          <FileInput
            source="kbMedia"
            label="KB Media (Video)"
            variant="outlined"
            fullWidth
            multiple
            placeholder={
              <p>
                <AttachFileIcon fontSize="large" /> <br /> Drop media file(s) to
                upload, or click to select.
              </p>
            }
            accept="video/*"
            onChange={(value) =>
              handleFileChange("kbMedia", value, form, notify)
            }
            helperText={false}
          >
            <FileField
              source="src"
              title="title"
              target="_blank"
              tokenPrefix="kbmedia"
              onAddToken={insertTokenIntoContent}
            />
          </FileInput>
        </Grid>
      </Grid>
    </Grid>
  );
};
